/**
 * Background Geolocation Service
 *
 * Handles background location tracking and geofencing for automatic job switching.
 * When a worker is clocked in, this service monitors their location and automatically
 * switches them to the correct job if they've been at a different job site for 15+ minutes.
 *
 * Also logs all ENTER/EXIT geofence events for location timeline and smart clock-out.
 */

import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import BackgroundGeolocation, {
  State,
  Config,
  Location,
  GeofenceEvent,
  Geofence
} from '@transistorsoft/capacitor-background-geolocation';
import { API_CONFIG } from '../config/api.config';
import { geofenceEventsDB, LocalGeofenceEvent } from '../db/geofence-events.db';
import { isNativeIOS } from '../utils/platform.utils';

// Shop job IDs - never auto-switch TO these
const SHOP_JOB_IDS = [1, 2];

// Geofence dwell time before triggering switch (15 minutes in milliseconds)
const DWELL_TIME_MS = 15 * 60 * 1000;

// Geofence refresh interval (1 hour)
const GEOFENCE_REFRESH_INTERVAL_MS = 60 * 60 * 1000;

interface JobGeofence {
  job_id: number;
  job_name: string;
  lat: number;
  lng: number;
  radius: number;
}

interface BackgroundGeolocationState {
  isInitialized: boolean;
  isTracking: boolean;
  currentJobId: number | null;
  currentEntryId: number | null;  // TIME_ENTRIES.entry_id for current shift
  employeeId: number | null;      // Current employee ID
  userRole: string | null;        // User's role (e.g., TRUCK_DRIVER, FIELD_WORKER)
  autoSwitchEnabled: boolean;     // Whether auto-switch is enabled for this user
  handledJobIds: Set<number>; // Jobs user already handled (ignored/switched) this session
  geofences: JobGeofence[];
  refreshIntervalId: NodeJS.Timeout | null;
}

// Debounce window for geofence events (5 minutes)
const GEOFENCE_DEBOUNCE_MS = 5 * 60 * 1000;

class BackgroundGeolocationService {
  private state: BackgroundGeolocationState = {
    isInitialized: false,
    isTracking: false,
    currentJobId: null,
    currentEntryId: null,
    employeeId: null,
    userRole: null,
    autoSwitchEnabled: true,
    handledJobIds: new Set(),
    geofences: [],
    refreshIntervalId: null
  };

  // Track last event per geofence to debounce flapping
  // Key: "job_{id}_{ENTER|EXIT}", Value: timestamp
  private lastEventTimes: Map<string, number> = new Map();

  /**
   * Check if background geolocation is available
   */
  isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Initialize the background geolocation plugin
   * Should be called once on app startup
   */
  async initialize(): Promise<boolean> {
    if (!this.isAvailable()) {
      console.log('[BackgroundGeolocation] Not available on web platform');
      return false;
    }

    if (this.state.isInitialized) {
      console.log('[BackgroundGeolocation] Already initialized');
      return true;
    }

    try {
      console.log('[BackgroundGeolocation] Initializing...');

      // License configuration:
      // - Android: License is in AndroidManifest.xml (meta-data)
      // - iOS: License is passed via config below

      // iOS License Key (Transistorsoft order #15337)
      // Note: iOS license passed via extended config, Android uses AndroidManifest.xml
      const iosLicenseKey = 'bc6a622e74ad8ee991d9529d8abc0f8db192049e2c2662f97148bd970c74c6c1';

      const config: Config = {
        // Force apply config on every launch — needed to overwrite stale persisted config
        // from previous versions that had conflicting stopOnTerminate/enableHeadless values
        reset: true,

        // Debug options - disable for production
        debug: false,
        logLevel: BackgroundGeolocation.LOG_LEVEL_OFF,

        // Geofence-only mode: we use startGeofences() instead of start()
        // No continuous location tracking — OS-native geofence monitoring only
        // This eliminates the Android blue navigation bar and saves maximum battery
        stopOnTerminate: true, // Stop when app is killed - prevents zombie Android service when clocked out
        startOnBoot: false, // Don't auto-start on device boot

        // iOS-specific
        preventSuspend: false, // Allow iOS to suspend - geofence monitoring works in suspended state
        locationAuthorizationRequest: 'Always', // Required for background geofence monitoring
        disableMotionActivityUpdates: true, // No motion detection needed for geofence-only
        disableLocationAuthorizationAlert: true, // Use iOS native prompts only

        // Android-specific
        enableHeadless: false, // No native headless task exists — was keeping zombie service alive
        // Background permission rationale dialog (Android 11+ only)
        ...(!isNativeIOS() ? {
          backgroundPermissionRationale: {
            title: 'Background Location Required',
            message: 'Bradanna Field needs "Allow all the time" location permission to automatically track which job site you\'re at while clocked in.',
            positiveAction: 'Open Settings',
            negativeAction: 'Cancel'
          }
        } : {}),

        // Geofence settings
        // iOS has a 20-geofence limit and dynamically monitors the nearest ones within this radius.
        // Using 5km instead of 3km gives more "lead time" - geofences activate while user is still
        // OUTSIDE the boundary, allowing iOS to properly fire ENTER when they cross into it.
        // With 3km, users often arrived inside geofences before monitoring started, missing ENTER events.
        geofenceProximityRadius: 5000, // 5km - larger radius to catch ENTER events before user is inside
        geofenceInitialTriggerEntry: false // Keep false to avoid continuous location tracking (blue arrow)
      };

      // Configure the plugin
      // Add iOS license key if on iOS (not in TypeScript types but accepted by native plugin)
      const configWithLicense = isNativeIOS()
        ? { ...config, license: iosLicenseKey } as Config
        : config;
      const state: State = await BackgroundGeolocation.ready(configWithLicense);
      console.log('[BackgroundGeolocation] Ready with state:', state.enabled);

      // Set up event listeners
      this.setupEventListeners();

      this.state.isInitialized = true;

      // Restore tracking state from localStorage (in case app was restarted mid-shift)
      const wasTracking = this.restoreTrackingState();

      // If we were tracking before app restart, verify with server and restart the plugin
      if (wasTracking) {
        console.log('[BackgroundGeolocation] Was tracking before restart, verifying with server...');
        try {
          // Check with server if user is still clocked in
          // (handles case where user clocked out on a different device)
          const token = localStorage.getItem('bfa_token');
          if (token) {
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TIME}/current`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (!data.success || !data.data?.job_id) {
              // User is NOT clocked in - clear stale tracking state
              console.log('[BackgroundGeolocation] Server says user is not clocked in, clearing stale tracking state');
              this.clearPersistedState();
              this.state.currentJobId = null;
              this.state.currentEntryId = null;
              this.state.employeeId = null;
              this.state.isTracking = false;
              return true;
            }

            // CRITICAL: Sync state from server to handle multi-device scenarios
            // User may have clocked in on a different device, so our localStorage is stale
            const serverJobId = data.data.job_id;
            const serverEntryId = data.data.entry_id;

            if (this.state.currentJobId !== serverJobId) {
              console.log(`[BackgroundGeolocation] Syncing job from server: ${this.state.currentJobId} → ${serverJobId}`);
              this.state.currentJobId = serverJobId;
            }
            if (this.state.currentEntryId !== serverEntryId) {
              console.log(`[BackgroundGeolocation] Syncing entry from server: ${this.state.currentEntryId} → ${serverEntryId}`);
              this.state.currentEntryId = serverEntryId;
            }

            // Update persisted state with server truth
            this.persistTrackingState();

            console.log('[BackgroundGeolocation] Server confirmed user is clocked in, restarting tracking...');
          }

          // Re-register geofences with forced location update, then start geofence-only monitoring
          await this.refreshGeofences(true); // Force location update
          await BackgroundGeolocation.startGeofences();
          this.state.isTracking = true;

          // Restart periodic geofence refresh
          this.startGeofenceRefresh();

          console.log('[BackgroundGeolocation] Successfully restored tracking after app restart');
        } catch (restartError) {
          console.error('[BackgroundGeolocation] Error restarting tracking after app restart:', restartError);
        }
      }

      return true;
    } catch (error) {
      console.error('[BackgroundGeolocation] Initialization error:', error);
      return false;
    }
  }

  /**
   * Set up geofence and location event listeners
   */
  private setupEventListeners(): void {
    // Listen for geofence events
    BackgroundGeolocation.onGeofence(async (event: GeofenceEvent) => {
      console.log('[BackgroundGeolocation] Geofence event:', event.action, event.identifier);
      await this.handleGeofenceEvent(event);
    });

    // Listen for provider changes (e.g., user disables location)
    BackgroundGeolocation.onProviderChange((event) => {
      console.log('[BackgroundGeolocation] Provider changed:', event);
    });
  }

  /**
   * Handle geofence events
   * Logs all ENTER/EXIT events for location timeline, and handles DWELL for auto-switching
   */
  private async handleGeofenceEvent(event: GeofenceEvent): Promise<void> {
    const { action, identifier, location } = event;

    // Parse the geofence identifier (format: "job_{job_id}")
    const match = identifier.match(/^job_(\d+)$/);
    if (!match) {
      console.log('[BackgroundGeolocation] Unknown geofence identifier:', identifier);
      return;
    }

    const jobId = parseInt(match[1], 10);
    console.log(`[BackgroundGeolocation] Geofence ${action} for job ${jobId}`);

    // Debounce: suppress duplicate ENTER/EXIT for same geofence within 5 minutes
    if (action === 'ENTER' || action === 'EXIT') {
      const debounceKey = `job_${jobId}_${action}`;
      const lastTime = this.lastEventTimes.get(debounceKey);
      const now = Date.now();

      if (lastTime && (now - lastTime) < GEOFENCE_DEBOUNCE_MS) {
        console.log(`[BackgroundGeolocation] Debounced ${action} for job ${jobId} (${Math.round((now - lastTime) / 1000)}s since last)`);
        return;
      }

      this.lastEventTimes.set(debounceKey, now);
      await this.logGeofenceEvent(jobId, action as 'ENTER' | 'EXIT', location);
    }

    // AUTO-SWITCH RETIRED (circle-based DWELL was unreliable for adjacent
    // sites). DWELL is no longer registered above, but hard-stop here too so
    // a stray DWELL from a device that hasn't refreshed its geofences can't
    // move someone's hours. ENTER/EXIT above already logged the timeline.
    // (To revive auto-switch: remove this guard AND restore notifyOnDwell.)
    if (action === 'DWELL') {
      console.log('[BackgroundGeolocation] DWELL ignored — auto-switch retired');
      return;
    }

    // Only handle DWELL events for auto-switching (been at location for 15+ minutes)
    if (action !== 'DWELL') {
      console.log(`[BackgroundGeolocation] ${action} event logged, waiting for DWELL for auto-switch`);
      return;
    }

    // Check if auto-switch is disabled for this user (via admin setting)
    if (!this.state.autoSwitchEnabled) {
      console.log('[BackgroundGeolocation] Auto-switch disabled for this user (admin setting)');
      return;
    }

    // Don't auto-switch for truck drivers - they move between jobs frequently
    if (this.state.userRole === 'TRUCK_DRIVER') {
      console.log('[BackgroundGeolocation] Auto-switch disabled for truck drivers');
      return;
    }

    // Don't switch if this is the current job
    if (jobId === this.state.currentJobId) {
      console.log('[BackgroundGeolocation] Already at current job, no switch needed');
      return;
    }

    // Don't switch to shop jobs
    if (SHOP_JOB_IDS.includes(jobId)) {
      console.log('[BackgroundGeolocation] Not switching to shop job');
      return;
    }

    // Don't switch if user already handled this job this session
    if (this.state.handledJobIds.has(jobId)) {
      console.log('[BackgroundGeolocation] Job already handled this session');
      return;
    }

    // Log AUTO_SWITCH event to timeline before performing the switch
    await this.logGeofenceEvent(jobId, 'AUTO_SWITCH', location);

    // Perform the job switch
    await this.performJobSwitch(jobId, location);
  }

  /**
   * Log a geofence event - sends to server immediately, falls back to local storage
   * @param providedTime - Optional ISO timestamp to use instead of current time (for CLOCK_OUT events)
   */
  private async logGeofenceEvent(
    jobId: number,
    eventType: 'ENTER' | 'EXIT' | 'CLOCK_IN' | 'CLOCK_OUT' | 'AUTO_SWITCH',
    location?: Location,
    providedTime?: string
  ): Promise<void> {
    // Must have entry ID and employee ID to log events
    if (!this.state.currentEntryId || !this.state.employeeId) {
      console.log('[BackgroundGeolocation] Cannot log event - missing entry or employee ID');
      return;
    }

    // Use provided time if available (for CLOCK_OUT), otherwise use current time
    const eventTime = providedTime || new Date().toISOString();
    const lat = location?.coords.latitude ?? null;
    const lng = location?.coords.longitude ?? null;

    // Find job name from geofences
    const geofence = this.state.geofences.find(g => g.job_id === jobId);
    const jobName = geofence?.job_name || `Job #${jobId}`;

    try {
      // Try to send to server immediately
      const token = localStorage.getItem('bfa_token');
      if (token) {
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TIME_ENTRIES}/geofence-event`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            entry_id: this.state.currentEntryId,
            job_id: jobId,
            event_type: eventType,
            event_time: eventTime,
            lat,
            lng
          })
        });

        const data = await response.json();
        if (data.success) {
          console.log(`[BackgroundGeolocation] Sent ${eventType} event to server for job ${jobId}, event_id: ${data.data.event_id}`);

          // Dispatch event so UI can update
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('geofenceEventLogged', {
              detail: { entryId: this.state.currentEntryId, jobId, jobName, eventType, eventTime, lat, lng }
            }));
          }
          return;
        }
        console.warn('[BackgroundGeolocation] Server rejected event:', data.error);
      }
    } catch (error) {
      console.warn('[BackgroundGeolocation] Failed to send event to server, storing locally:', error);
    }

    // Fallback: Store locally for sync on clock out
    try {
      const event = {
        entryId: this.state.currentEntryId,
        employeeId: this.state.employeeId,
        jobId,
        jobName,
        eventType,
        eventTime,
        lat,
        lng
      };

      const eventId = await geofenceEventsDB.addEvent(event);
      console.log(`[BackgroundGeolocation] Stored ${eventType} event locally for job ${jobId}, eventId: ${eventId}`);

      // Dispatch event so UI can update
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('geofenceEventLogged', {
          detail: { ...event, id: eventId }
        }));
      }
    } catch (error) {
      console.error('[BackgroundGeolocation] Error storing geofence event locally:', error);
    }
  }

  /**
   * Perform the actual job switch
   */
  private async performJobSwitch(newJobId: number, location?: Location): Promise<void> {
    try {
      console.log(`[BackgroundGeolocation] Switching to job ${newJobId}...`);

      const token = localStorage.getItem('bfa_token');
      if (!token) {
        console.error('[BackgroundGeolocation] No auth token available');
        return;
      }

      const timezoneOffsetMinutes = new Date().getTimezoneOffset();
      const isFromShop = SHOP_JOB_IDS.includes(this.state.currentJobId || 0);
      const conversionReason = isFromShop ? 'shop_to_job' : 'job_to_job';

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TIME_ENTRIES}/switch-job`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          job_id: newJobId,
          lat: location?.coords.latitude || null,
          lng: location?.coords.longitude || null,
          conversion_reason: conversionReason,
          timezone_offset_minutes: timezoneOffsetMinutes,
          billing_method: 'split',
          switch_source: 'background'
        })
      });

      const data = await response.json();

      if (data.success) {
        console.log('[BackgroundGeolocation] Successfully switched to job:', newJobId);

        // Find the job name from our geofences
        const geofence = this.state.geofences.find(g => g.job_id === newJobId);
        const jobName = geofence?.job_name || `Job #${newJobId}`;

        // Update current job ID and entry ID
        const oldJobId = this.state.currentJobId;
        this.state.currentJobId = newJobId;

        // Update entry ID to the new entry created by the switch
        if (data.data?.new_entry_id) {
          console.log(`[BackgroundGeolocation] Updating entry ID: ${this.state.currentEntryId} → ${data.data.new_entry_id}`);
          this.state.currentEntryId = data.data.new_entry_id;
          this.persistTrackingState();
        }

        // Show notification
        await this.showNotification(`Switched to ${jobName}`);

        // Dispatch event so UI components can update
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('backgroundJobSwitch', {
            detail: {
              oldJobId,
              newJobId,
              jobName,
              timestamp: new Date().toISOString()
            }
          }));

          // Also dispatch the existing jobSwitched event for compatibility
          window.dispatchEvent(new CustomEvent('jobSwitched', {
            detail: { job_id: newJobId, job_name: jobName }
          }));
        }
      } else {
        console.error('[BackgroundGeolocation] Failed to switch job:', data.error);
      }
    } catch (error) {
      console.error('[BackgroundGeolocation] Error switching job:', error);
      // Queue for retry when online
      // TODO: Implement offline queue
    }
  }

  /**
   * Show a local notification
   */
  private async showNotification(message: string): Promise<void> {
    try {
      // Use the plugin's notification system
      // Note: On iOS, notifications may need additional permission
      console.log('[BackgroundGeolocation] Notification:', message);

      // For now, we rely on the plugin's built-in notification
      // and the UI update via the dispatched event
    } catch (error) {
      console.error('[BackgroundGeolocation] Error showing notification:', error);
    }
  }

  /**
   * Start background tracking
   * Called when user clocks in
   * @param jobId - Current job ID
   * @param entryId - TIME_ENTRIES.entry_id for this shift
   * @param employeeId - Employee ID
   * @param userRole - User's role (e.g., TRUCK_DRIVER) - affects auto-switch behavior
   * @param trackingEnabled - Whether background tracking is enabled for this user (default true)
   * @param autoSwitchEnabled - Whether auto-switch is enabled for this user (default true)
   */
  async startTracking(
    jobId: number,
    entryId?: number,
    employeeId?: number,
    userRole?: string,
    trackingEnabled: boolean = true,
    autoSwitchEnabled: boolean = true
  ): Promise<boolean> {
    // Check if tracking is disabled for this user
    if (!trackingEnabled) {
      console.log('[BackgroundGeolocation] Background tracking disabled for this user');
      return false;
    }

    if (!this.isAvailable()) {
      console.log('[BackgroundGeolocation] Not available, using fallback');
      return false;
    }

    if (!this.state.isInitialized) {
      const initialized = await this.initialize();
      if (!initialized) {
        return false;
      }
    }

    try {
      console.log(`[BackgroundGeolocation] Starting tracking for job ${jobId}, entry ${entryId}, employee ${employeeId}, role ${userRole}, autoSwitch ${autoSwitchEnabled}`);

      // Reset session state
      this.state.currentJobId = jobId;
      this.state.currentEntryId = entryId ?? null;
      this.state.employeeId = employeeId ?? null;
      this.state.userRole = userRole ?? null;
      this.state.autoSwitchEnabled = autoSwitchEnabled;
      this.state.handledJobIds.clear();

      // Persist tracking state to localStorage (survives app restart)
      this.persistTrackingState();

      // Fetch and register geofences with forced location update for accurate proximity
      await this.refreshGeofences(true); // Force location update

      // Start geofence-only monitoring (no continuous location tracking)
      const state = await BackgroundGeolocation.startGeofences();
      console.log('[BackgroundGeolocation] Started geofence-only mode:', state);

      this.state.isTracking = true;

      // Log CLOCK_IN synthetic event
      if (entryId && employeeId) {
        await this.logClockInEvent(jobId, entryId, employeeId);
      }

      // Set up periodic geofence refresh
      this.startGeofenceRefresh();

      return true;
    } catch (error) {
      console.error('[BackgroundGeolocation] Error starting tracking:', error);
      return false;
    }
  }

  /**
   * Persist tracking state to localStorage (survives app restart)
   */
  private persistTrackingState(): void {
    const trackingState = {
      currentJobId: this.state.currentJobId,
      currentEntryId: this.state.currentEntryId,
      employeeId: this.state.employeeId,
      userRole: this.state.userRole,
      autoSwitchEnabled: this.state.autoSwitchEnabled,
      isTracking: this.state.isTracking
    };
    localStorage.setItem('bgGeo_trackingState', JSON.stringify(trackingState));
    console.log('[BackgroundGeolocation] Persisted tracking state:', trackingState);
  }

  /**
   * Restore tracking state from localStorage (on app restart)
   */
  private restoreTrackingState(): boolean {
    try {
      const stored = localStorage.getItem('bgGeo_trackingState');
      if (!stored) return false;

      const trackingState = JSON.parse(stored);
      if (trackingState.currentEntryId && trackingState.employeeId) {
        this.state.currentJobId = trackingState.currentJobId;
        this.state.currentEntryId = trackingState.currentEntryId;
        this.state.employeeId = trackingState.employeeId;
        this.state.userRole = trackingState.userRole || null;
        this.state.autoSwitchEnabled = trackingState.autoSwitchEnabled !== false; // default true
        console.log('[BackgroundGeolocation] Restored tracking state:', trackingState);
        return true;
      }
    } catch (error) {
      console.error('[BackgroundGeolocation] Error restoring tracking state:', error);
    }
    return false;
  }

  /**
   * Clear persisted tracking state
   */
  private clearPersistedState(): void {
    localStorage.removeItem('bgGeo_trackingState');
    console.log('[BackgroundGeolocation] Cleared persisted tracking state');
  }

  /**
   * Log a CLOCK_IN synthetic event
   */
  private async logClockInEvent(jobId: number, entryId: number, employeeId: number): Promise<void> {
    // Temporarily set state for logGeofenceEvent
    this.state.currentEntryId = entryId;
    this.state.employeeId = employeeId;

    try {
      // Get current location for the event
      let location: Location | undefined;
      try {
        location = await BackgroundGeolocation.getCurrentPosition({ samples: 1, timeout: 5 });
      } catch (e) {
        console.log('[BackgroundGeolocation] Could not get location for CLOCK_IN event');
      }

      await this.logGeofenceEvent(jobId, 'CLOCK_IN', location);
    } catch (error) {
      console.error('[BackgroundGeolocation] Error logging CLOCK_IN event:', error);
    }
  }

  /**
   * Log a CLOCK_OUT synthetic event
   * @param clockOutTime - Optional ISO timestamp of actual clock out time
   */
  private async logClockOutEvent(jobId: number, entryId: number, employeeId: number, clockOutTime?: string): Promise<void> {
    // Ensure state is set for logGeofenceEvent
    this.state.currentEntryId = entryId;
    this.state.employeeId = employeeId;

    try {
      // Get current location for the event
      let location: Location | undefined;
      try {
        location = await BackgroundGeolocation.getCurrentPosition({ samples: 1, timeout: 5 });
      } catch (e) {
        console.log('[BackgroundGeolocation] Could not get location for CLOCK_OUT event');
      }

      await this.logGeofenceEvent(jobId, 'CLOCK_OUT', location, clockOutTime);
    } catch (error) {
      console.error('[BackgroundGeolocation] Error logging CLOCK_OUT event:', error);
    }
  }

  /**
   * Update entry ID after clock in response
   * Called when we get the entry_id from the clock in API
   */
  setEntryId(entryId: number): void {
    this.state.currentEntryId = entryId;
    this.persistTrackingState();
    console.log(`[BackgroundGeolocation] Set entry ID to ${entryId}`);
  }

  /**
   * Set employee ID
   */
  setEmployeeId(employeeId: number): void {
    this.state.employeeId = employeeId;
    this.persistTrackingState();
    console.log(`[BackgroundGeolocation] Set employee ID to ${employeeId}`);
  }

  /**
   * Stop background tracking
   * Called when user clocks out
   * @param clockOutTime - Optional ISO timestamp of actual clock out time (prevents delayed logging issues)
   */
  async stopTracking(clockOutTime?: string): Promise<void> {
    if (!this.isAvailable() || !this.state.isTracking) {
      return;
    }

    try {
      console.log('[BackgroundGeolocation] Stopping tracking');

      // Log CLOCK_OUT synthetic event before clearing state
      // Use provided clockOutTime to ensure accurate timestamp even if this runs later
      if (this.state.currentEntryId && this.state.employeeId && this.state.currentJobId) {
        await this.logClockOutEvent(
          this.state.currentJobId,
          this.state.currentEntryId,
          this.state.employeeId,
          clockOutTime
        );
      }

      // Stop the plugin
      await BackgroundGeolocation.stop();

      // Remove all geofences
      await BackgroundGeolocation.removeGeofences();

      // Stop geofence refresh
      this.stopGeofenceRefresh();

      // Reset state
      this.state.isTracking = false;
      this.state.currentJobId = null;
      this.state.currentEntryId = null;
      this.state.employeeId = null;
      this.state.userRole = null;
      this.state.autoSwitchEnabled = true;
      this.state.handledJobIds.clear();
      this.state.geofences = [];
      this.lastEventTimes.clear();

      // Clear persisted state from localStorage
      this.clearPersistedState();

      console.log('[BackgroundGeolocation] Stopped');
    } catch (error) {
      console.error('[BackgroundGeolocation] Error stopping tracking:', error);
    }
  }

  /**
   * Get all unsynced geofence events for the current entry
   * Called before clock out to include events in the request
   */
  async getPendingEvents(): Promise<LocalGeofenceEvent[]> {
    if (!this.state.currentEntryId) {
      console.log('[BackgroundGeolocation] No current entry ID, returning empty events');
      return [];
    }

    try {
      const events = await geofenceEventsDB.getUnsyncedEvents(this.state.currentEntryId);
      console.log(`[BackgroundGeolocation] Found ${events.length} pending events for entry ${this.state.currentEntryId}`);
      return events;
    } catch (error) {
      console.error('[BackgroundGeolocation] Error getting pending events:', error);
      return [];
    }
  }

  /**
   * Get the last EXIT event from the clocked-in job
   * Used for smart "forgot to clock out" suggestions
   */
  async getLastExitFromCurrentJob(): Promise<LocalGeofenceEvent | undefined> {
    if (!this.state.currentEntryId || !this.state.currentJobId) {
      return undefined;
    }

    try {
      return await geofenceEventsDB.getLastExitEvent(
        this.state.currentEntryId,
        this.state.currentJobId
      );
    } catch (error) {
      console.error('[BackgroundGeolocation] Error getting last exit event:', error);
      return undefined;
    }
  }

  /**
   * Get the last EXIT event from any job for the current entry
   * Fallback for smart clock-out when current job exit not found
   */
  async getLastExitEvent(): Promise<LocalGeofenceEvent | undefined> {
    if (!this.state.currentEntryId) {
      return undefined;
    }

    try {
      return await geofenceEventsDB.getLastExitEventForEntry(this.state.currentEntryId);
    } catch (error) {
      console.error('[BackgroundGeolocation] Error getting last exit event:', error);
      return undefined;
    }
  }

  /**
   * Clear all events for the current entry after successful sync
   */
  async clearEventsForCurrentEntry(): Promise<void> {
    if (!this.state.currentEntryId) {
      return;
    }

    try {
      await geofenceEventsDB.clearEventsForEntry(this.state.currentEntryId);
      console.log(`[BackgroundGeolocation] Cleared events for entry ${this.state.currentEntryId}`);
    } catch (error) {
      console.error('[BackgroundGeolocation] Error clearing events:', error);
    }
  }

  /**
   * Mark a job as handled (user ignored or manually switched)
   * This prevents the service from auto-switching to this job again this session
   */
  markJobAsHandled(jobId: number): void {
    this.state.handledJobIds.add(jobId);
    console.log(`[BackgroundGeolocation] Marked job ${jobId} as handled`);
  }

  /**
   * Fetch active jobs and register their geofences
   * @param forceLocationUpdate - Force get current position to ensure accurate proximity calculation
   */
  async refreshGeofences(forceLocationUpdate: boolean = false): Promise<void> {
    try {
      console.log('[BackgroundGeolocation] Refreshing geofences...');

      // Force location update to ensure proximity calculation uses current position
      if (forceLocationUpdate) {
        try {
          console.log('[BackgroundGeolocation] Forcing location update for proximity calculation...');
          const location = await BackgroundGeolocation.getCurrentPosition({
            samples: 3,
            timeout: 15,
            desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH
          });
          console.log(`[BackgroundGeolocation] Got location: ${location.coords.latitude}, ${location.coords.longitude} (accuracy: ${location.coords.accuracy}m)`);
        } catch (locError) {
          console.warn('[BackgroundGeolocation] Could not get location for proximity refresh:', locError);
        }
      }

      const token = localStorage.getItem('bfa_token');
      if (!token) {
        console.error('[BackgroundGeolocation] No auth token available');
        return;
      }

      // Fetch active jobs
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.JOBS}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!data.success || !data.data) {
        console.error('[BackgroundGeolocation] Failed to fetch jobs:', data.error);
        return;
      }

      // Filter jobs with valid coordinates (include shops for ENTER/EXIT tracking)
      const jobsWithCoords = data.data.filter((job: any) =>
        job.lat && job.lng
      );

      // Remove existing geofences
      await BackgroundGeolocation.removeGeofences();

      // Create geofences for each job
      // Shops: ENTER/EXIT only (no DWELL to prevent auto-switch attempts)
      // Jobs: ENTER/EXIT/DWELL for auto-switching
      const geofences: Geofence[] = jobsWithCoords.map((job: any) => {
        const isShop = SHOP_JOB_IDS.includes(job.job_id);
        return {
          identifier: `job_${job.job_id}`,
          latitude: job.lat,
          longitude: job.lng,
          radius: Math.max(job.geofence_data?.radius || 200, 200), // Minimum 200m — Android GPS jitter causes flapping on smaller geofences
          notifyOnEntry: true,
          notifyOnExit: true,
          // AUTO-SWITCH RETIRED: DWELL is what triggered auto-switching the
          // clocked-in job after 15 min at a site. iOS only does circular
          // background regions, and overlapping/adjacent circles made it
          // switch hours to the wrong neighbouring job — unreliable. Disabled
          // by never registering DWELL. ENTER/EXIT stay on so the location
          // timeline keeps working. (To revive: restore `!isShop` here.)
          notifyOnDwell: false,
          loiteringDelay: DWELL_TIME_MS, // 15 minutes (only relevant if DWELL re-enabled)
          extras: {
            job_id: job.job_id,
            job_name: job.job_name,
            is_shop: isShop
          }
        };
      });

      // Add geofences
      if (geofences.length > 0) {
        await BackgroundGeolocation.addGeofences(geofences);
        console.log(`[BackgroundGeolocation] Registered ${geofences.length} geofences:`);
        geofences.forEach(g => {
          console.log(`  ${g.identifier}: radius=${g.radius}m, entry=${g.notifyOnEntry}, exit=${g.notifyOnExit}, dwell=${g.notifyOnDwell}`);
        });
      }

      // Store geofence data for reference
      this.state.geofences = jobsWithCoords.map((job: any) => ({
        job_id: job.job_id,
        job_name: job.job_name,
        lat: job.lat,
        lng: job.lng,
        radius: job.geofence_data?.radius || 100
      }));
    } catch (error) {
      console.error('[BackgroundGeolocation] Error refreshing geofences:', error);
    }
  }

  /**
   * Start periodic geofence refresh
   */
  private startGeofenceRefresh(): void {
    if (this.state.refreshIntervalId) {
      clearInterval(this.state.refreshIntervalId);
    }

    this.state.refreshIntervalId = setInterval(() => {
      this.refreshGeofences(true); // Force location update on periodic refresh
    }, GEOFENCE_REFRESH_INTERVAL_MS);
  }

  /**
   * Stop periodic geofence refresh
   */
  private stopGeofenceRefresh(): void {
    if (this.state.refreshIntervalId) {
      clearInterval(this.state.refreshIntervalId);
      this.state.refreshIntervalId = null;
    }
  }

  /**
   * Update the current job ID
   * Called when user manually switches jobs
   */
  updateCurrentJob(jobId: number): void {
    console.log(`[BackgroundGeolocation] Updated current job to ${jobId}`);
    this.state.currentJobId = jobId;
  }

  /**
   * Update current entry ID (after a job switch creates a new time entry)
   */
  updateCurrentEntryId(entryId: number): void {
    console.log(`[BackgroundGeolocation] Updated current entry ID: ${this.state.currentEntryId} → ${entryId}`);
    this.state.currentEntryId = entryId;
    this.persistTrackingState();
  }

  /**
   * Get current tracking state
   */
  getState(): {
    isTracking: boolean;
    currentJobId: number | null;
    currentEntryId: number | null;
    employeeId: number | null;
  } {
    return {
      isTracking: this.state.isTracking,
      currentJobId: this.state.currentJobId,
      currentEntryId: this.state.currentEntryId,
      employeeId: this.state.employeeId
    };
  }

  /**
   * Force refresh geofences with current location
   * Useful for debugging or when geofence monitoring seems stuck
   */
  async forceRefreshGeofences(): Promise<void> {
    if (!this.state.isTracking) {
      console.log('[BackgroundGeolocation] Not tracking, skipping refresh');
      return;
    }
    console.log('[BackgroundGeolocation] Force refreshing geofences...');
    await this.refreshGeofences(true);
  }

  /**
   * Ensure tracking is active if user is clocked in
   * Called on app resume to verify TransistorSoft is running
   * @param clockedInData - Data from /api/time/current if user is clocked in
   * @param userRole - User's role for auto-switch behavior
   * @param trackingEnabled - Whether background tracking is enabled for this user
   * @param autoSwitchEnabled - Whether auto-switch is enabled for this user
   */
  async ensureTrackingActive(
    clockedInData: { job_id: number; entry_id: number; employee_id: number } | null,
    userRole?: string,
    trackingEnabled: boolean = true,
    autoSwitchEnabled: boolean = true
  ): Promise<void> {
    if (!this.isAvailable()) {
      return;
    }

    // If not clocked in, nothing to do
    if (!clockedInData) {
      console.log('[BackgroundGeolocation] ensureTrackingActive: Not clocked in, skipping');
      return;
    }

    // Check if plugin is actually running
    try {
      const pluginState = await BackgroundGeolocation.getState();

      if (pluginState.enabled && this.state.isTracking) {
        console.log('[BackgroundGeolocation] ensureTrackingActive: Plugin already running');
        return;
      }

      // Plugin not running but user is clocked in - restart it!
      console.log('[BackgroundGeolocation] ensureTrackingActive: Plugin NOT running, restarting...');
      console.log(`[BackgroundGeolocation] Job: ${clockedInData.job_id}, Entry: ${clockedInData.entry_id}, Employee: ${clockedInData.employee_id}`);

      // Initialize if needed
      if (!this.state.isInitialized) {
        await this.initialize();
      }

      // Start tracking with the current clock-in data
      await this.startTracking(
        clockedInData.job_id,
        clockedInData.entry_id,
        clockedInData.employee_id,
        userRole,
        trackingEnabled,
        autoSwitchEnabled
      );

      console.log('[BackgroundGeolocation] ensureTrackingActive: Successfully restarted tracking');
    } catch (error) {
      console.error('[BackgroundGeolocation] ensureTrackingActive: Error checking/restarting:', error);
    }
  }

  /**
   * Request necessary permissions
   * Returns true if all permissions were granted
   */
  async requestPermissions(): Promise<boolean> {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      // The plugin handles permission requests internally
      // This method can be used to explicitly request before starting
      const status = await BackgroundGeolocation.requestPermission();
      console.log('[BackgroundGeolocation] Permission status:', status);
      return status === BackgroundGeolocation.AUTHORIZATION_STATUS_ALWAYS;
    } catch (error) {
      console.error('[BackgroundGeolocation] Error requesting permissions:', error);
      return false;
    }
  }
}

// Export singleton instance
export const backgroundGeolocationService = new BackgroundGeolocationService();
